import React, { useId } from "react"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { OverflowScrollButtons, useOverflowScroll } from "../overflow-scroll"
import { tabsBaseContainerStyles, tabsBaseListStyles } from "../tabs-base"
import { VisuallyHidden } from "../visually-hidden"
import { cn } from "../utils"
import { useTabsOrientation } from "./tabs-context"

// =============================================================================
// Props
// =============================================================================

export type TabsListProps = {
  /**
   * The accessible name for the tab list, describing what the tabs switch
   * between (e.g. "Product settings").
   */
  accessibleName: string
  /** The `Tabs.Tab`s */
  children?: React.ReactNode
  /** Layout classes for the list's container, such as margin */
  className?: string
  /**
   * Whether moving focus with the arrow keys also selects the tab. When
   * `false`, the focused tab is selected with Enter or Space. Turn it on when
   * every panel shows without delay.
   */
  activateOnFocus?: boolean
}

// =============================================================================
// Component
// =============================================================================

/**
 * The row of tabs, rendered as a `role="tablist"` named by `accessibleName`.
 *
 * Horizontal tabs scroll when they overflow and fade at the edges, where a
 * button scrolls them by a page for pointer users. The selected tab and a
 * keyboard-focused tab are scrolled clear of the fades.
 *
 * Must be a descendant of `Tabs`.
 */
export function TabsList({
  accessibleName,
  children,
  className,
  activateOnFocus = false
}: TabsListProps) {
  const orientation = useTabsOrientation()
  const accessibleNameId = useId()
  const { scrollerProps, scrollButtonsProps } =
    useOverflowScroll<HTMLDivElement>({
      activeAttribute: "data-active",
      disabled: orientation === "vertical"
    })

  return (
    <div
      className={cn(tabsBaseContainerStyles({ orientation }), className)}
      data-slot="tabs-list"
    >
      <VisuallyHidden id={accessibleNameId}>{accessibleName}</VisuallyHidden>
      <TabsPrimitive.List
        {...scrollerProps}
        activateOnFocus={activateOnFocus}
        aria-labelledby={accessibleNameId}
        className={cn(tabsBaseListStyles({ orientation }))}
        data-slot="tabs-list-tablist"
      >
        {children}
      </TabsPrimitive.List>
      <OverflowScrollButtons
        {...scrollButtonsProps}
        data-slot="tabs-list-scroll-button"
      />
    </div>
  )
}

TabsList.displayName = "Tabs.List"
